import { useState } from 'react';

import { DEFAULT_TOPIC } from 'pages/ParentHelper/constants';

import { Provider } from './context';
import {
  useSendFeedback,
  useGetChatHistory,
  useSendQuestion,
  useGetChatTopics,
  useDeleteChat,
} from './hooks';

import type { ParentHelperContext } from './types';
import type { ChatId, Message } from 'pages/ParentHelper/types';
import type { FC, PropsWithChildren as ParentHelperProviderProps } from 'react';

export const ParentHelperProvider: FC<ParentHelperProviderProps> = ({
  children,
}) => {
  const [data, setData] = useState<Message[]>([]);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [chatId, setChatId] = useState<ChatId>(null);
  const [chatTopics, setChatTopics] = useState([DEFAULT_TOPIC]);

  const {
    isLoading: isLoadingChatTopics,
    isLoadingMore,
    loadMore,
    data: chatTopicsData,
  } = useGetChatTopics(setChatId, setChatTopics);

  const { isLoadingChat, isErrorChat } = useGetChatHistory({
    setData,
    setChatId,
    chatId,
    setShouldScrollToBottom,
  });

  const { sendQuestion, isLoadingAnswer, isErrorSendQuestion } =
    useSendQuestion({
      chatTopics,
      setChatTopics,
      setData,
      chatId,
      setChatId,
      setShouldScrollToBottom,
    });

  const { likeMessage, dislikeMessage } = useSendFeedback(
    setData,
    setShouldScrollToBottom,
  );

  const { deleteChat, setDeletedChatId, deletedChatId } = useDeleteChat({
    chatId,
    setChatId,
    chatTopics,
    setChatTopics,
  });

  const value: ParentHelperContext = {
    data,
    isLoadingChat,
    isLoadingAnswer,
    isError: isErrorSendQuestion || isErrorChat,
    chatId,
    isLoadingChatTopics,
    chatTopics,
    setChatId,
    setChatTopics,
    isLoadingMore,
    loadMore,
    chatTopicsData,
    shouldScrollToBottom,
    sendQuestion,
    likeMessage,
    dislikeMessage,
    deleteChat,
    setDeletedChatId,
    deletedChatId, // TODO: remove after adding Notistack
  };

  return <Provider value={value}>{children}</Provider>;
};
